import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtRelative } from "@/lib/format";
import { KanbanBoard } from "@/lib/kanban";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { List, LayoutGrid, AlertTriangle, ChevronUp, Minus, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const PRIO_ICON = { urgent: AlertTriangle, high: ChevronUp, medium: Minus, low: ChevronDown };
const PRIO_COLOR = { urgent: "text-red-500", high: "text-amber-500", medium: "text-muted-foreground", low: "text-muted-foreground/60" };

function ServiceCard({ item, problemTypes }) {
  const PrioIcon = PRIO_ICON[item.priority] || Minus;
  const pt = problemTypes.find((p) => p.id === item.problem_type_id);
  return (
    <Link to={`/service/${item.id}`} onClick={(e) => e.target.closest('[draggable]') && e.preventDefault()}>
      <div
        className="bg-card border border-border rounded-lg p-3 shadow-sm hover:shadow-md hover:border-foreground/20 transition-all"
        data-testid={`service-card-${item.service_number}`}
      >
        <div className="flex items-start justify-between mb-2">
          <div className="font-mono text-[10px] text-muted-foreground">{item.service_number}</div>
          <PrioIcon className={`h-3.5 w-3.5 ${PRIO_COLOR[item.priority]}`} />
        </div>
        <div className="text-sm font-medium truncate mb-1">{item.customer_name}</div>
        <div className="text-xs text-muted-foreground">#{item.order_number}</div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
          <Badge variant="outline" className="text-[10px] px-1.5 py-0">{pt?.name || "—"}</Badge>
          <span className="text-[10px] text-muted-foreground">{fmtRelative(item.created_at)}</span>
        </div>
      </div>
    </Link>
  );
}

export default function ServiceKanbanPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [problemTypes, setProblemTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [r, s, p] = await Promise.all([
        api.get("/service"),
        api.get("/service/statuses"),
        api.get("/service/problem-types"),
      ]);
      setItems(r.data);
      setStatuses(s.data);
      setProblemTypes(p.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onMove = async (itemId, fromStatusId, toStatusId) => {
    setItems((cur) => cur.map((r) => (r.id === itemId ? { ...r, status_id: toStatusId } : r)));
    try {
      await api.patch(`/service/${itemId}`, { status_id: toStatusId });
      const newStatus = statuses.find((s) => s.id === toStatusId);
      toast.success(`Mutat în "${newStatus?.name}"`);
    } catch {
      setItems((cur) => cur.map((r) => (r.id === itemId ? { ...r, status_id: fromStatusId } : r)));
      toast.error("Nu am putut schimba statusul");
    }
  };

  return (
    <div className="space-y-6" data-testid="service-kanban-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Modul · Service</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Kanban</h1>
          <p className="text-muted-foreground text-sm mt-1">Trage și mută cardurile între statusuri.</p>
        </div>
        <Tabs value="kanban">
          <TabsList>
            <TabsTrigger value="list" onClick={() => navigate("/service")}><List className="h-4 w-4 mr-1.5" /> Listă</TabsTrigger>
            <TabsTrigger value="kanban"><LayoutGrid className="h-4 w-4 mr-1.5" /> Kanban</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {loading ? (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {Array.from({ length: 4 }).map((_, i) => (<Skeleton key={i} className="w-80 h-[420px] shrink-0" />))}
        </div>
      ) : (
        <KanbanBoard
          statuses={statuses}
          items={items}
          renderCard={(item) => <ServiceCard item={item} problemTypes={problemTypes} />}
          onMove={onMove}
        />
      )}
    </div>
  );
}
